'use client';
import React from 'react';
import { useTranslations } from 'next-intl';

import type { RestaurantReview } from '@/lib/types/customer/restaurant/StoreReview';
import type { AvgTotalRating } from '@/lib/types/customer/restaurant/AvgTotalRating';

import Button from '@/components/ui/Button';
import ShowAllReviewsModal from './ShowAllReviewsModal';
import OverallRatingCard from './OverallRatingCard';
import SingleReview from './SingleReview';
import { ActiveSubscription } from '@/lib/types/customer/restaurant/ActiveSubscription';
import { isBasicSubscriber, isPaidSubscriber } from '@/lib/helpers/functions';
import If from '@ui/If';
import NotAvailableCard from '@/shared/NotAvailableCard';
import { Store } from '@/lib/types/customer/restaurant/StoreProfile';

type Props = {
  store: Store;
  reviews: RestaurantReview[];
  avgTotalRating: AvgTotalRating;
  storeName: string;
  thumb_url: string;
  activeSubscription: ActiveSubscription;
};

export default function RatingAndReview({
  store,
  reviews,
  avgTotalRating,
  storeName,
  thumb_url,
  activeSubscription,
}: Props) {
  const [showAllReviewsModal, setShowAllReviewsModal] =
    React.useState<boolean>(false);

  const handleShowAllReviews = () => {
    setShowAllReviewsModal(true);
  };

  const t = useTranslations('');

  if (!store.features.handle_reviews) {
    return <></>;
  }

  if (!reviews || reviews.length <= 0 || isBasicSubscriber(activeSubscription))
    return <></>;

  return (
    <section className="py-10">
      <div className="mb-6 flex flex-col">
        <h3 className="text-h3 font-[900] leading-[140%] text-title">
          {t('storefront.store_profile.rating_and_reviews')}
        </h3>
        <If
          condition={reviews && reviews.length > 0}
          fallback={<p className="mt-6">{<NotAvailableCard />}</p>}
        >
          <OverallRatingCard
            avgTotalRating={avgTotalRating}
            rounded
            storeName={storeName}
          />
          {reviews.slice(0, 3).map((review) => (
            <SingleReview
              review={review}
              key={review.id}
              storeName={storeName}
              thumb_url={thumb_url}
            />
          ))}
          {reviews.length > 3 && (
            <Button
              rounded
              className="mt-[60px] w-fit self-start hover:drop-shadow-md"
              onClick={() => handleShowAllReviews()}
            >
              {t('storefront.store_profile.view_more')}
            </Button>
          )}

          {/* same design just wrapped inside a modal */}
          {showAllReviewsModal && (
            <ShowAllReviewsModal
              isOpen={showAllReviewsModal}
              closeModal={() => setShowAllReviewsModal(false)}
            >
              <div className="mb-6 flex flex-col">
                <h3 className="px-[40px] pt-[40px] text-h3 font-[900] leading-[140%] text-title">
                  {t('storefront.store_profile.rating_and_reviews')}
                </h3>
                <OverallRatingCard
                  avgTotalRating={avgTotalRating}
                  storeName={storeName}
                />
                <div className="p-[40px]">
                  {reviews.map((review) => (
                    <SingleReview
                      review={review}
                      key={review.id}
                      storeName={storeName}
                      thumb_url={thumb_url}
                    />
                  ))}
                </div>
              </div>
            </ShowAllReviewsModal>
          )}
        </If>
      </div>
    </section>
  );
}
